import { useState } from 'react';
import { useInstallPrompt } from './useInstallPrompt';
import { Button } from '../ui/Button';

/**
 * Shown on the login screen, which is where a device is set up.
 *
 * Renders nothing once the app is running installed — `display-mode:
 * standalone` on Android, `navigator.standalone` on iOS — so a working shop
 * device never sees it.
 */
export function InstallBanner() {
  const state = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (state.kind === 'installed' || state.kind === 'unavailable') return null;

  if (state.kind === 'prompt') {
    return (
      <div className="rounded-md bg-accent-subtle p-4">
        <p className="text-sm font-medium text-accent-text">Install this app</p>
        <p className="mt-1 text-xs text-secondary">
          Add Shringaar to the home screen so it opens full-screen, without browser controls.
        </p>
        <div className="mt-3 flex gap-2">
          <Button
            size="md"
            onClick={() => {
              void state.install();
            }}
          >
            Install
          </Button>
          <Button size="md" variant="ghost" onClick={() => setDismissed(true)}>
            Not now
          </Button>
        </div>
      </div>
    );
  }

  // iOS has no install API at all — the only thing we can do is describe the
  // manual steps. Chrome and Firefox on iOS cannot install a PWA even manually,
  // so `useInstallPrompt` has already filtered them out and this only renders
  // in Safari.
  return (
    <div className="rounded-md bg-accent-subtle p-4">
      <p className="text-sm font-medium text-accent-text">Add to Home Screen</p>
      <ol className="mt-2 flex list-decimal flex-col gap-1 pl-4 text-xs text-secondary">
        <li>
          Tap the <span className="font-semibold">Share</span> button in Safari's toolbar — the
          square with an arrow pointing up.
        </li>
        <li>
          Scroll down and tap <span className="font-semibold">Add to Home Screen</span>.
        </li>
        <li>Open Shringaar from the new home-screen icon, not from Safari.</li>
      </ol>
      <button
        type="button"
        className="mt-3 text-xs font-medium text-accent-text underline"
        onClick={() => setDismissed(true)}
      >
        Dismiss
      </button>
    </div>
  );
}
